import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import SectionHeader from './SectionHeader';
import PhoneNumberInput from './PhoneNumberInput';
import TeamMemberEmailField from './TeamMemberEmailField';
import { formatPhoneForDisplay } from '../utils/phoneNumber';
import { teamMemberEmailPayload } from '../utils/teamMemberEmail';
import './TransactionTeam.css';
import { getContractApiUrl } from '../utils/apiUrls';
import useViewerSubscription from '../hooks/useViewerSubscription';

const BACKEND_URL = getContractApiUrl();

const ROLES = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'Referring Agent', label: 'Referring Agent' },
  { value: 'Receiving Agent', label: 'Receiving Agent' },
  { value: 'sellers_agent', label: "Seller's Agent" },
  { value: 'buyer_agent', label: "Buyer's Agent" },
  { value: 'sellers_transaction_coordinator', label: "Seller's Transaction Coordinator" },
  { value: 'buyers_transaction_coordinator', label: "Buyer's Transaction Coordinator" },
  { value: 'title_officer', label: 'Title Officer' },
  { value: 'escrow_officer', label: 'Escrow Officer' },
  { value: 'participant', label: 'Participant' },
];

const getRoleLabel = (roleVal) => {
  const match = ROLES.find((r) => r.value === roleVal || r.label === roleVal);
  if (match) return match.label;
  return roleVal || 'Unknown';
};

const normalizeRoleValue = (role) => {
  if (!role) return 'participant';
  const match = ROLES.find((r) => r.value === role || r.label === role);
  return match ? match.value : role;
};

const isPartyMember = (member) => {
  const role = (member.role || '').toLowerCase().replace(/[\s-]+/g, '_');
  return (
    member.source === 'skyslope_placeholder'
    || /tbd/i.test(member.role || '')
    || role === 'buyer'
    || role === 'seller'
    || role === 'co_buyer'
    || role === 'co_seller'
  );
};

const ReferralAgreementTeam = ({
  referralAgreementId,
  team: initialTeam = [],
  userEmail,
  isExpanded,
  onToggle,
}) => {
  const { user } = useAuth0();
  const { isGuest, loaded: viewerLoaded } = useViewerSubscription();
  const canEditTeam = viewerLoaded && isGuest !== true;
  const callerEmail = userEmail || user?.email;
  const teamRevisionRef = useRef(0);
  const [team, setTeam] = useState(initialTeam || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    role: 'Referring Agent',
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => {
    if (initialTeam) setTeam(initialTeam);
  }, [initialTeam]);

  const fetchTeam = async () => {
    if (!referralAgreementId || !callerEmail) return;
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/referral-agreements/${referralAgreementId}/team`, {
        params: { user_email: callerEmail },
      });
      return res.data;
    } catch (err) {
      console.error('Error fetching referral team:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!referralAgreementId || !isExpanded || !callerEmail) return undefined;

    let cancelled = false;
    const revisionAtFetch = teamRevisionRef.current;
    (async () => {
      const data = await fetchTeam();
      if (!cancelled && data && revisionAtFetch === teamRevisionRef.current) {
        setTeam(data);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [referralAgreementId, isExpanded, callerEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingMember(null);
    setFormData({
      role: 'Referring Agent',
      name: '',
      email: '',
      phone: '',
      company: '',
    });
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      role: normalizeRoleValue(member.role),
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      company: member.company || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!callerEmail) return;
    teamRevisionRef.current += 1;
    try {
      setLoading(true);
      if (editingMember) {
        const res = await axios.put(
          `${BACKEND_URL}/referral-agreements/${referralAgreementId}/team/${editingMember.id}`,
          {
            role: formData.role,
            name: formData.name,
            ...teamMemberEmailPayload(editingMember, formData.email),
            phone: formData.phone,
            company: formData.company,
            requester_email: callerEmail,
          },
        );
        setTeam((prev) => prev.map((m) => (m.id === editingMember.id ? res.data : m)));
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/referral-agreements/${referralAgreementId}/team`,
          {
            role: formData.role,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            requester_email: callerEmail,
          },
        );
        setTeam((prev) => [...prev, res.data]);
      }
      setShowModal(false);
      setEditingMember(null);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingMember || !window.confirm('Remove this team member?')) return;
    teamRevisionRef.current += 1;
    try {
      setLoading(true);
      await axios.delete(
        `${BACKEND_URL}/referral-agreements/${referralAgreementId}/team/${editingMember.id}`,
        { params: { user_email: callerEmail } },
      );
      setTeam((prev) => prev.filter((m) => m.id !== editingMember.id));
      setShowModal(false);
      setEditingMember(null);
    } catch (err) {
      alert('Failed to remove: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!referralAgreementId) return null;

  const parties = team.filter(isPartyMember);
  const agents = team.filter((m) => !isPartyMember(m));

  const renderCard = (member, variant = 'standard') => {
    const isMinimal = variant === 'minimal';
    return (
      <div key={member.id || `${member.role}-${member.name}`} className={`team-member-card ${isMinimal ? 'card-minimal' : 'card-branded'}`}>
        {!isMinimal && (
          <div className="card-custom-header">
            <span className="header-role">{getRoleLabel(member.role)}</span>
            {canEditTeam && (
            <button
              type="button"
              className="edit-icon-btn header-edit"
              onClick={(e) => { e.stopPropagation(); openEditModal(member); }}
            >
              ✎
            </button>
            )}
          </div>
        )}
        <div className="member-info">
          <div className="member-name-row">
            <div className="member-name-group">
              <span className="member-name">{member.name || 'Unknown Name'}</span>
              {member.company && <span className="member-company">{member.company}</span>}
            </div>
            {isMinimal && canEditTeam && (
              <button
                type="button"
                className="edit-icon-btn"
                onClick={(e) => { e.stopPropagation(); openEditModal(member); }}
              >
                ✎
              </button>
            )}
          </div>
          {member.email && <span className="member-email">{member.email}</span>}
          {member.phone && <span className="member-email">{formatPhoneForDisplay(member.phone)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="transaction-team-container">
      <SectionHeader title="Key People" isExpanded={isExpanded} onToggle={onToggle}>
        <div className="section-controls">
          {canEditTeam && (
          <button
            type="button"
            className="add-member-header-btn"
            onClick={(e) => { e.stopPropagation(); openAddModal(); }}
            title="Add Team Member"
          >
            + Add Member
          </button>
          )}
        </div>
      </SectionHeader>

      {isExpanded && (
        <div className="team-content">
          {loading && <div className="loading">Loading key people...</div>}
          {!loading && !team.length && (
            <p className="no-team-members">No team members yet.</p>
          )}
          {team.length > 0 && (
            <div
              className="key-people-grid"
              style={{ gridTemplateColumns: parties.length ? '1fr 2fr' : '1fr' }}
            >
              {parties.length > 0 && (
                <div className="people-column column-buyer">
                  <div className="column-card">
                    <h3 className="column-header column-header-navy">Transaction Parties</h3>
                    <div className="column-content">
                      {parties.map((m) => renderCard(m, 'minimal'))}
                    </div>
                  </div>
                </div>
              )}
              <div className="people-column column-others">
                <div className="column-card">
                  <h3 className="column-header column-header-green">Referral Team</h3>
                  <div className="column-content grid-others">
                    {(agents.length ? agents : []).map((m) => renderCard(m, 'standard'))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</h3>
              <button type="button" className="close-modal-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <select name="role" value={formData.role} onChange={handleInputChange}>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Name <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Company / Brokerage</label>
                <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company name" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <TeamMemberEmailField
                  member={editingMember}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <PhoneNumberInput name="phone" value={formData.phone} onChange={handleInputChange} placeholder="e.g. 405-555-1234" />
              </div>
              <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                <div>
                  {editingMember && (
                    <button type="button" className="btn-danger" onClick={handleDelete}>Remove Member</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingMember ? 'Save Changes' : 'Add Member')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralAgreementTeam;
